import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './toast-override.css'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Leads from './pages/Leads'
import LeadsAdmin from './pages/LeadsAdmin'
import Pipeline from './pages/Pipeline'
import Activities from './pages/Activities'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Documentation from './pages/Documentation'
import Duplicates from './pages/Duplicates'
import Admin from './pages/Admin'
import ManageSchool from './pages/ManageSchool'
import ParentApp from './pages/ParentApp'
import ErpLogin from './pages/parent-app/ErpLogin'
import ErpRouteGuard from './utils/auth/ErpRouteGuard'
import { isErpHost, erpPath } from './utils/erp-host'
import Login from './pages/Login'
import LoginOTP from './pages/LoginOTP'
import LoginEmail from './pages/LoginEmail'
import Register from './pages/Register'
import VerifyOTP from './pages/VerifyOTP'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import SetPassword from './pages/SetPassword'
import FacebookCallback from './pages/FacebookCallback'
import GoogleCallback from './pages/GoogleCallback'
import CrmProvision from './pages/CrmProvision'
import RouteGuard from './utils/auth/RouteGuard'
import { CallingProvider } from './contexts/CallingContext'
import { UserProvider } from './contexts/UserContext'
import { UsersProvider } from './contexts/UsersContext'

// DeliverHub imports
import DeliverHubLayout from './layouts/DeliverHubLayout'
import DeliveryHubHome from './pages/delivery-hub'
import SchoolDetail from './pages/delivery-hub/SchoolDetail'
import DeliveryHubQueries from './pages/delivery-hub/Queries'

// Public Review
import PublicReview from './pages/PublicReview'

// Logout
import Logout from './pages/Logout'

// Client Deliverables
import ClientDeliverables from './pages/ClientDeliverables'

// Map any legacy/aliased ERP path (/parent-app/* or /erp/*) to the canonical one
// for the current host: /erp/* on the CRM domain, root-level on the erp.* subdomain.
function toErpCanonical(pathname: string, search: string): string {
  const sub = pathname.replace(/^\/(parent-app|erp)(?=\/|$)/, '').replace(/^\/+/, '')
  return erpPath(sub) + search
}
function ErpLegacyRedirect() {
  const location = useLocation()
  return <Navigate to={toErpCanonical(location.pathname, location.search)} replace />
}

function App() {
  return (
    <CallingProvider>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 9999 }}
        />
        {isErpHost() ? (
        /* ── erp.yellowslate.com — ERP mounted at root, no CRM chrome ── */
        <Routes>
          <Route path="/login" element={<ErpLogin />} />
          {/* Any /erp/* or /parent-app/* link on this host collapses to root */}
          <Route path="/erp/*" element={<ErpLegacyRedirect />} />
          <Route path="/parent-app/*" element={<ErpLegacyRedirect />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/*" element={
            <ErpRouteGuard>
              <ParentApp />
            </ErpRouteGuard>
          } />
        </Routes>
        ) : (
        <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/login" element={
          <RouteGuard requireAuth={false}>
            <Login />
          </RouteGuard>
        } />
        <Route path="/login-otp" element={
          <RouteGuard requireAuth={false}>
            <LoginOTP />
          </RouteGuard>
        } />
        <Route path="/login-email" element={
          <RouteGuard requireAuth={false}>
            <LoginEmail />
          </RouteGuard>
        } />
        <Route path="/register" element={
          <RouteGuard requireAuth={false}>
            <Register />
          </RouteGuard>
        } />
        <Route path="/verify-otp" element={
          <RouteGuard requireAuth={false}>
            <VerifyOTP />
          </RouteGuard>
        } />
        <Route path="/forgot-password" element={
          <RouteGuard requireAuth={false}>
            <ForgotPassword />
          </RouteGuard>
        } />
        <Route path="/reset-password" element={
          <RouteGuard requireAuth={false}>
            <ResetPassword />
          </RouteGuard>
        } />
        <Route path="/set-password" element={
          <RouteGuard requireAuth={false}>
            <SetPassword />
          </RouteGuard>
        } />
        <Route path="/auth/facebook/success" element={
          <RouteGuard requireAuth={false}>
            <FacebookCallback />
          </RouteGuard>
        } />
        <Route path="/auth/google/callback" element={
          <RouteGuard requireAuth={false}>
            <GoogleCallback />
          </RouteGuard>
        } />
        <Route path="/auth/google/success" element={
          <RouteGuard requireAuth={false}>
            <GoogleCallback />
          </RouteGuard>
        } />
        <Route path="/crm-provision" element={
          <RouteGuard requireAuth={false}>
            <CrmProvision />
          </RouteGuard>
        } />
        {/* Public review page - no auth check, accessible by anyone */}
        <Route path="/review/:token" element={<PublicReview />} />

        {/* Logout route - handles logout for all apps */}
        <Route path="/logout" element={<Logout />} />

        {/* Protected Routes - Authentication required */}
        <Route path="/" element={
          <RouteGuard requireAuth={true}>
            <UserProvider>
              <UsersProvider>
                <Layout />
              </UsersProvider>
            </UserProvider>
          </RouteGuard>
        }>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads_admin" element={<LeadsAdmin />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="duplicates" element={<Duplicates />} />
          <Route path="activities" element={<Activities />} />
          <Route path="reports/*" element={<Reports />} />
          <Route path="manage-school/:id/*" element={<ManageSchool />} />
          <Route path="admin/*" element={<Admin />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="docs/*" element={<Documentation />} />
          <Route path="client-deliverables" element={<ClientDeliverables />} />
        </Route>

        {/* School ERP — dedicated staff login (public) */}
        <Route path="/erp/login" element={<ErpLogin />} />
        {/* School ERP Portal - Standalone, no CRM chrome. Accepts ERP staff token or CRM session. */}
        <Route path="/erp/*" element={
          <ErpRouteGuard>
            <ParentApp />
          </ErpRouteGuard>
        } />
        {/* Legacy /parent-app/* links redirect to /erp/* (preserves subpath) */}
        <Route path="/parent-app/*" element={<ErpLegacyRedirect />} />

        {/* DeliveryHub Routes - Separate layout (sidebar, no top nav) */}
        <Route path="/delivery-hub" element={
          <RouteGuard requireAuth={true}>
            <DeliverHubLayout />
          </RouteGuard>
        }>
          <Route index element={<DeliveryHubHome />} />
          <Route path="queries" element={<DeliveryHubQueries />} />
          <Route path="schools/:schoolId" element={<SchoolDetail />} />
        </Route>
      </Routes>
        )}
      </div>
    </CallingProvider>
  )
}

export default App